import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Package, Layers, DollarSign, AlertTriangle, Search, Plus, Edit, Trash2,
  Minus, Download, RefreshCw, LayoutGrid, Table as TableIcon, ShoppingBag, Loader2,
  ChevronLeft, ChevronRight, Eraser, QrCode, CheckSquare, Square, Award, Boxes,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import CardModal from "@/components/CardModal";
import SlabModal from "@/components/SlabModal";
import QrPrintModal from "@/components/QrPrintModal";
import TrialBanner from "@/components/TrialBanner";

const CONDITION_STYLES = {
  NM: "bg-emerald-950/80 text-emerald-400 border-emerald-800/60",
  LP: "bg-blue-950/80 text-blue-400 border-blue-800/60",
  MP: "bg-amber-950/80 text-amber-400 border-amber-800/60",
  HP: "bg-orange-950/80 text-orange-400 border-orange-800/60",
  DMG: "bg-rose-950/80 text-rose-400 border-rose-800/60",
};

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
import { useSubscription } from "@/context/SubscriptionContext";
const GAME_BADGE = { pokemon_japan: "JP", pokemon_china: "CN", custom: "CUSTOM", magic: "MTG", onepiece: "OP", riftbound: "RIFT", lorcana: "LOR", yugioh: "YGO" };
const imgUrl = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function Dashboard() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [setFilter, setSetFilter] = useState("");
  const [condFilter, setCondFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const { gameOptions } = useSubscription();
  const [thinOnly, setThinOnly] = useState(false);
  const [boxedOnly, setBoxedOnly] = useState(false);
  const [view, setView] = useState("table");
  const [modalOpen, setModalOpen] = useState(false);
  const [multiMode, setMultiMode] = useState(false);
  const [slabOpen, setSlabOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState(new Set());
  const [qrOpen, setQrOpen] = useState(false);
  const [qrCards, setQrCards] = useState([]);

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleSelectAll = (list) => setSelected((prev) => {
    const n = new Set(prev);
    const all = list.length > 0 && list.every((c) => n.has(c.id));
    if (all) list.forEach((c) => n.delete(c.id));
    else list.forEach((c) => n.add(c.id));
    return n;
  });
  const openQr = (list) => { setQrCards(list); setQrOpen(true); };
  const printSelected = () => {
    const list = cards.filter((c) => selected.has(c.id));
    if (!list.length) { toast.error("Select one or more cards to print"); return; }
    openQr(list);
  };
  const handleSaved = (saved, isNew) => {
    refetchAll();
    if (isNew && saved?.qr_token && !multiMode) openQr([saved]);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debounced, setFilter, condFilter, categoryFilter, typeFilter, sortBy, thinOnly, boxedOnly, pageSize]);

  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: sets = [] } = useQuery({ queryKey: ["sets", categoryFilter, typeFilter], queryFn: async () => (await api.get("/cards/sets", { params: { category: categoryFilter, item_type: typeFilter } })).data });
  const { data: cardData, isLoading } = useQuery({
    queryKey: ["cards", debounced, setFilter, condFilter, categoryFilter, typeFilter, sortBy, thinOnly, boxedOnly, page, pageSize],
    queryFn: async () => (await api.get("/cards", { params: { search: debounced, set_name: setFilter, condition: condFilter, category: categoryFilter, item_type: typeFilter, sort: sortBy, price_source: thinOnly ? "lowest" : "", boxed_only: boxedOnly, page, page_size: pageSize } })).data,
    keepPreviousData: true,
  });
  const cards = cardData?.items ?? [];
  const total = cardData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const allSelected = cards.length > 0 && cards.every((c) => selected.has(c.id));

  const refetchAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["cards"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    qc.invalidateQueries({ queryKey: ["sets"] });
  }, [qc]);

  const adjustQty = async (card, delta) => {
    const next = Math.max(0, (card.quantity || 0) + delta);
    try {
      await api.put(`/cards/${card.id}`, { quantity: next });
      if (delta < 0 && card.on_shopify) toast.success(`Synced ${card.name} to Shopify`);
      refetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const recordSale = async (card, platform) => {
    try {
      const { data } = await api.post("/sales", { card_id: card.id, platform, quantity: 1 });
      const synced = data.synced_platforms?.length ? ` · synced to ${data.synced_platforms.join(", ")}` : "";
      toast.success(`Sold 1 ${card.name} on ${platform}${synced}`);
      refetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (card) => {
    if (!window.confirm(`Delete "${card.name}"?`)) return;
    try { await api.delete(`/cards/${card.id}`); toast.success("Card deleted"); refetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear your ENTIRE inventory? This permanently deletes every card and cannot be undone.")) return;
    if (!window.confirm("Are you absolutely sure? All cards will be gone.")) return;
    try {
      const { data } = await api.delete("/cards");
      toast.success(`Cleared ${data.deleted} cards`);
      setPage(1);
      refetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    window.open(`${API}/csv/export`, "_blank");
  };

  const mergeDupes = async () => {
    try {
      const { data } = await api.post("/cards/dedupe");
      toast.success(data.merged ? `Merged ${data.merged} duplicate line${data.merged > 1 ? "s" : ""}` : "No duplicates found");
      setPage(1);
      refetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statCards = [
    { label: "Total Cards", value: stats?.total_cards ?? 0, icon: Package, color: "text-amber-400", testid: "stat-total-cards" },
    { label: "Units in Stock", value: stats?.total_quantity ?? 0, icon: Layers, color: "text-cyan-400", testid: "stat-total-qty" },
    { label: "Inventory Value", value: fmt(stats?.total_value), icon: DollarSign, color: "text-emerald-400", testid: "stat-total-value" },
    { label: "Low Stock", value: stats?.low_stock_count ?? 0, icon: AlertTriangle, color: "text-rose-400", testid: "stat-low-stock-alert" },
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <TrialBanner />
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Inventory Command Center</h1>
          <p className="text-sm text-slate-400 mt-1">Manage every single across Shopify & TCGPlayer from one vault.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSlabOpen(true)} data-testid="inventory-add-slab-button"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-violet-500/60 text-violet-300 font-semibold text-sm hover:bg-violet-500/10 transition-colors">
            <Award className="h-4 w-4" /> Add Slab
          </button>
          <button onClick={() => { setEditing(null); setMultiMode(true); setModalOpen(true); }} data-testid="inventory-add-multiple-button"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-amber-500/60 text-amber-400 font-semibold text-sm hover:bg-amber-500/10 transition-colors">
            <Layers className="h-4 w-4" /> Add Multiple
          </button>
          <button onClick={() => { setEditing(null); setMultiMode(false); setModalOpen(true); }} data-testid="inventory-add-card-button"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 transition-colors">
            <Plus className="h-4 w-4" /> Add Card
          </button>
        </div>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
        {statCards.map((s) => (
          <div key={s.label} data-testid={s.testid} className="stat-gradient rounded-2xl border p-5 animate-fade-up" style={{ borderColor: "var(--pk-border)" }}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</span>
              <s.icon className={`h-4 w-4 ${s.color}`} />
            </div>
            <div className="text-2xl sm:text-3xl font-bold font-mono-num text-slate-100 mt-3">{s.value}</div>
          </div>
        ))}
      </div>

      {/* toolbar */}
      <div className="rounded-2xl border p-4 mb-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="inventory-game-chips">
          {gameOptions.map(([v, l]) => (
            <button key={v || "all"} onClick={() => { setCategoryFilter(v); setSetFilter(""); }} data-testid={`game-chip-${v || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${categoryFilter === v ? "bg-amber-500 text-[#1a1205] border-amber-500" : "text-slate-400 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
              {v === "" ? "All Games" : l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 min-w-[200px] focus-within:ring-2 focus-within:ring-amber-500/50"
               style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <Search className="h-4 w-4 text-slate-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, SKU, number..."
              data-testid="inventory-search-input" className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
          </div>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setSetFilter(""); }} data-testid="inventory-category-filter"
            className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            {gameOptions.map(([v, l]) => <option key={v} value={v} style={{ background: "#0D1017" }}>{v === "" ? "All Categories" : l}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setSetFilter(""); }} data-testid="inventory-type-filter"
            className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <option value="" style={{ background: "#0D1017" }}>All Types</option>
            <option value="raw" style={{ background: "#0D1017" }}>Singles</option>
            <option value="slab" style={{ background: "#0D1017" }}>Slabs</option>
            <option value="sealed" style={{ background: "#0D1017" }}>Sealed</option>
          </select>
          <select value={setFilter} onChange={(e) => setSetFilter(e.target.value)} data-testid="inventory-set-filter"
            className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none min-w-0 w-full max-w-full sm:w-auto sm:max-w-[200px] truncate" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <option value="" style={{ background: "#0D1017" }}>All Sets</option>
            {sets.map((s) => <option key={s} value={s} style={{ background: "#0D1017" }}>{s}</option>)}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="inventory-sort-filter"
            className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <option value="newest" style={{ background: "#0D1017" }}>Newest → Oldest</option>
            <option value="oldest" style={{ background: "#0D1017" }}>Oldest → Newest</option>
            <option value="price_high" style={{ background: "#0D1017" }}>Price: High → Low</option>
            <option value="price_low" style={{ background: "#0D1017" }}>Price: Low → High</option>
            <option value="az" style={{ background: "#0D1017" }}>Name: A → Z</option>
            <option value="za" style={{ background: "#0D1017" }}>Name: Z → A</option>
          </select>
          <select value={condFilter} onChange={(e) => setCondFilter(e.target.value)} data-testid="inventory-condition-filter"
            className="px-3 py-2 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            <option value="" style={{ background: "#0D1017" }}>All Conditions</option>
            {["NM", "LP", "MP", "HP", "DMG"].map((c) => <option key={c} value={c} style={{ background: "#0D1017" }}>{c}</option>)}
          </select>
          <button onClick={() => setThinOnly((v) => !v)} data-testid="inventory-thin-data-filter" title="Show only played cards priced off the single lowest listing (thin data)"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${thinOnly ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "text-slate-300 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
            <AlertTriangle className="h-4 w-4" /> Thin Data{thinOnly ? " ✓" : ""}
          </button>
          <button onClick={() => setBoxedOnly((v) => !v)} data-testid="inventory-boxed-filter" title="Show only inventory cards Boxed.gg is currently buying"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${boxedOnly ? "bg-blue-500/15 text-blue-400 border-blue-500/40" : "text-slate-300 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
            <Boxes className="h-4 w-4" /> Boxed.gg{boxedOnly ? " ✓" : ""}
          </button>
          <button onClick={mergeDupes} data-testid="inventory-merge-dupes-btn" title="Combine duplicate lines (same card, condition & printing) into one, summing quantities"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
            <Layers className="h-4 w-4" /> Merge Dupes
          </button>
          <button onClick={exportCsv} data-testid="batch-export-tcgplayer-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={clearAll} data-testid="inventory-clear-all-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-800/60 text-sm text-rose-400 hover:bg-rose-500/10">
            <Eraser className="h-4 w-4" /> Clear Inventory
          </button>
          <button onClick={() => toggleSelectAll(cards)} disabled={cards.length === 0} data-testid="inventory-select-all-btn"
            title={allSelected ? "Deselect all cards on this page" : "Select all cards on this page"}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${allSelected ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "text-slate-200 border-[var(--pk-border)] hover:bg-[var(--pk-hover)]"}`}>
            {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />} {allSelected ? "Deselect All" : "Select All"}{selected.size ? ` (${selected.size})` : ""}
          </button>
          <button onClick={printSelected} data-testid="inventory-print-qr-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-slate-200 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
            <QrCode className="h-4 w-4" /> Print QR{selected.size ? ` (${selected.size})` : ""}
          </button>
          <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: "var(--pk-border)" }}>
            <button onClick={() => setView("table")} data-testid="view-toggle-table" className={`p-2 ${view === "table" ? "bg-amber-500/15 text-amber-400" : "text-slate-500"}`}><TableIcon className="h-4 w-4" /></button>
            <button onClick={() => setView("grid")} data-testid="view-toggle-grid" className={`p-2 ${view === "grid" ? "bg-amber-500/15 text-amber-400" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {/* list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : cards.length === 0 ? (
        <Empty onAdd={() => { setEditing(null); setMultiMode(false); setModalOpen(true); }} />
      ) : view === "table" ? (
        <TableView cards={cards} onEdit={(c) => { setEditing(c); setMultiMode(false); setModalOpen(true); }} onDelete={remove}
          adjustQty={adjustQty} recordSale={recordSale} selected={selected} onToggle={toggleSelect} onPrint={(c) => openQr([c])} />
      ) : (
        <GridView cards={cards} onEdit={(c) => { setEditing(c); setMultiMode(false); setModalOpen(true); }} onDelete={remove}
          adjustQty={adjustQty} recordSale={recordSale} selected={selected} onToggle={toggleSelect} onPrint={(c) => openQr([c])} />
      )}

      {cards.length > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} count={cards.length} />
      )}

      <CardModal open={modalOpen} card={editing} multi={multiMode} onClose={() => { setModalOpen(false); setMultiMode(false); }} onSaved={handleSaved}
        onMultiDone={(list) => { const printable = (list || []).filter((c) => c?.qr_token); if (printable.length) openQr(printable); }} />
      <SlabModal open={slabOpen} onClose={() => setSlabOpen(false)} onSaved={handleSaved} />
      <QrPrintModal open={qrOpen} cards={qrCards} onClose={() => setQrOpen(false)} />
    </div>
  );
}

function CondBadge({ c }) {
  return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${CONDITION_STYLES[c] || CONDITION_STYLES.NM}`}>{c}</span>;
}

function GradeOrCond({ card }) {
  if (card.item_type === "slab") {
    return <span data-testid="grade-badge" title={`${card.grading_company || "PSA"} graded slab${card.cert_number ? ` · cert ${card.cert_number}` : ""}`}
      className="px-2 py-0.5 rounded-md text-[11px] font-bold border bg-violet-950/80 text-violet-300 border-violet-700/60 whitespace-nowrap">{card.grade || card.grading_company || "SLAB"}</span>;
  }
  return <CondBadge c={card.condition} />;
}

function SourceTag({ card }) {
  const cond = (card.condition || "NM").toUpperCase();
  if (cond === "NM" || card.price_override) return null;
  const src = card.price_source;
  if (src === "lowest")
    return <span data-testid="price-source-tag" title="Priced off the single lowest listing — thin data, may need a manual check" className="ml-1.5 px-1 py-px rounded text-[9px] font-bold uppercase tracking-wide bg-amber-500/15 text-amber-400 border border-amber-500/30">low</span>;
  if (src === "median")
    return <span data-testid="price-source-tag" title="Priced off the median of real listings (tcgapi.dev)" className="ml-1.5 px-1 py-px rounded text-[9px] font-bold uppercase tracking-wide bg-slate-600/25 text-slate-400 border border-slate-600/40">med</span>;
  if (src === "market-est")
    return <span data-testid="price-source-tag" title="Estimated from market price × your condition multiplier (no live listings for this game/condition)" className="ml-1.5 px-1 py-px rounded text-[9px] font-bold uppercase tracking-wide bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">est</span>;
  return null;
}


function PlatformBadges({ card }) {
  return (
    <div className="flex gap-1.5">
      {card.on_shopify && <span title="On Shopify" className="inline-flex items-center justify-center h-5 w-5 rounded bg-emerald-950/80 border border-emerald-800/60"><ShoppingBag className="h-3 w-3 text-emerald-400" /></span>}
      {card.on_tcgplayer && <span title="On TCGPlayer" className="inline-flex items-center justify-center h-5 px-1.5 rounded bg-blue-950/80 border border-blue-800/60 text-[9px] font-bold text-blue-400">TCG</span>}
    </div>
  );
}

function QtyControls({ card, adjustQty }) {
  const low = card.quantity <= (card.low_stock_threshold ?? 1);
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => adjustQty(card, -1)} data-testid="card-qty-decrement-btn" className="h-6 w-6 rounded flex items-center justify-center border text-slate-400 hover:text-rose-400 hover:border-rose-500/50" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3 w-3" /></button>
      <span className={`w-9 text-center font-mono-num text-sm font-semibold ${low ? "text-rose-400" : "text-slate-100"}`}>{card.quantity}</span>
      <button onClick={() => adjustQty(card, 1)} data-testid="card-qty-increment-btn" className="h-6 w-6 rounded flex items-center justify-center border text-slate-400 hover:text-emerald-400 hover:border-emerald-500/50" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3 w-3" /></button>
    </div>
  );
}

function SellButtons({ card, recordSale }) {
  return (
    <div className="flex gap-1">
      <button onClick={() => recordSale(card, "shopify")} disabled={card.quantity < 1} title="Record Shopify sale"
        className="px-2 py-1 rounded text-[10px] font-semibold bg-emerald-950/60 text-emerald-400 border border-emerald-800/50 hover:bg-emerald-900/60 disabled:opacity-40">Sold Shopify</button>
      <button onClick={() => recordSale(card, "tcgplayer")} disabled={card.quantity < 1} title="Record TCGPlayer sale"
        className="px-2 py-1 rounded text-[10px] font-semibold bg-blue-950/60 text-blue-400 border border-blue-800/50 hover:bg-blue-900/60 disabled:opacity-40">Sold TCG</button>
    </div>
  );
}

function TableView({ cards, onEdit, onDelete, adjustQty, recordSale, selected, onToggle, onPrint }) {
  return (
    <>
    <div className="hidden md:block rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: "var(--pk-border)" }}>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-4 py-3 font-semibold">Card</th>
              <th className="px-4 py-3 font-semibold">Set / #</th>
              <th className="px-4 py-3 font-semibold">Cond</th>
              <th className="px-4 py-3 font-semibold">Qty</th>
              <th className="px-4 py-3 font-semibold">TCG $</th>
              <th className="px-4 py-3 font-semibold">Shopify $</th>
              <th className="px-4 py-3 font-semibold">Channels</th>
              <th className="px-4 py-3 font-semibold">Sell</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c) => (
              <tr key={c.id} data-testid="card-row-item" className="border-b hover:bg-[var(--pk-hover)] transition-colors" style={{ borderColor: "var(--pk-border-subtle)" }}>
                <td className="px-3 py-3">
                  <button onClick={() => onToggle(c.id)} data-testid="card-select-checkbox" className="text-slate-500 hover:text-amber-400">
                    {selected?.has(c.id) ? <CheckSquare className="h-4 w-4 text-amber-400" /> : <Square className="h-4 w-4" />}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-8 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                      {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="h-full w-full object-cover" /> : <Package className="h-4 w-4 text-slate-700" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-slate-100 font-medium truncate max-w-[200px]">{c.name}{GAME_BADGE[c.category] && <span className="ml-1.5 px-1 py-0.5 rounded bg-indigo-950/80 border border-indigo-800/60 text-[9px] font-bold text-indigo-300">{GAME_BADGE[c.category]}</span>}</div>
                      {c.sku && <div className="text-[11px] text-slate-500 font-mono-num">{c.sku}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-400"><div>{c.set_name || "—"}</div>{c.card_number && <div className="text-[11px] text-slate-600 font-mono-num">#{c.card_number}</div>}</td>
                <td className="px-4 py-3"><GradeOrCond card={c} /></td>
                <td className="px-4 py-3"><QtyControls card={c} adjustQty={adjustQty} /></td>
                <td className="px-4 py-3 font-mono-num text-slate-300"><span className="inline-flex items-center">{fmt(c.tcg_price)}<SourceTag card={c} /></span></td>
                <td className="px-4 py-3 font-mono-num text-slate-300">{fmt(c.shopify_price)}</td>
                <td className="px-4 py-3"><PlatformBadges card={c} /></td>
                <td className="px-4 py-3"><SellButtons card={c} recordSale={recordSale} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onPrint(c)} data-testid="card-print-qr-button" title="Print QR sticker" className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10"><QrCode className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onEdit(c)} data-testid="card-edit-button" className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-amber-500/10"><Edit className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onDelete(c)} className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-t text-[11px] text-slate-500" style={{ borderColor: "var(--pk-border)" }} data-testid="price-source-legend">
        <span className="font-semibold uppercase tracking-wider">Price source:</span>
        <span className="inline-flex items-center gap-1.5"><span className="px-1 py-px rounded text-[9px] font-bold uppercase bg-slate-600/25 text-slate-400 border border-slate-600/40">med</span> live listing median</span>
        <span className="inline-flex items-center gap-1.5"><span className="px-1 py-px rounded text-[9px] font-bold uppercase bg-amber-500/15 text-amber-400 border border-amber-500/30">low</span> single lowest listing</span>
        <span className="inline-flex items-center gap-1.5"><span className="px-1 py-px rounded text-[9px] font-bold uppercase bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">est</span> market × condition estimate</span>
        <span className="text-slate-600">· NM &amp; overrides show no tag</span>
      </div>
    </div>
    {/* Mobile stacked cards */}
    <div className="md:hidden space-y-3">
      {cards.map((c) => (
        <MobileCard key={c.id} c={c} onEdit={onEdit} onDelete={onDelete} adjustQty={adjustQty}
          recordSale={recordSale} selected={selected} onToggle={onToggle} onPrint={onPrint} />
      ))}
    </div>
    </>
  );
}

function MobileCard({ c, onEdit, onDelete, adjustQty, recordSale, selected, onToggle, onPrint }) {
  return (
    <div data-testid="card-row-item" className="rounded-2xl border p-4" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
      <div className="flex gap-3">
        <button onClick={() => onToggle(c.id)} data-testid="card-select-checkbox" className="text-slate-500 hover:text-amber-400 pt-0.5 shrink-0">
          {selected?.has(c.id) ? <CheckSquare className="h-5 w-5 text-amber-400" /> : <Square className="h-5 w-5" />}
        </button>
        <div className="h-16 w-12 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
          {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-700" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-slate-100 font-semibold truncate">{c.name}{GAME_BADGE[c.category] && <span className="ml-1.5 px-1 py-0.5 rounded bg-indigo-950/80 border border-indigo-800/60 text-[9px] font-bold text-indigo-300">{GAME_BADGE[c.category]}</span>}</div>
              <div className="text-[11px] text-slate-500 truncate">{c.set_name || "—"}{c.card_number ? ` · #${c.card_number}` : ""}</div>
            </div>
            <GradeOrCond card={c} />
          </div>
          <div className="mt-1.5"><PlatformBadges card={c} /></div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">TCG · Shopify</span>
          <span className="font-mono-num text-sm text-slate-200 inline-flex items-center">{fmt(c.tcg_price)}<SourceTag card={c} /> · {fmt(c.shopify_price)}</span>
        </div>
        <QtyControls card={c} adjustQty={adjustQty} />
      </div>
      <div className="mt-3"><SellButtons card={c} recordSale={recordSale} /></div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => onEdit(c)} data-testid="card-edit-button" className="flex-1 py-2.5 rounded-lg text-sm text-slate-200 border hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>Edit</button>
        <button onClick={() => onPrint(c)} data-testid="card-print-qr-button" title="Print QR" className="py-2.5 px-4 rounded-lg text-cyan-400 border hover:bg-cyan-500/10" style={{ borderColor: "var(--pk-border)" }}><QrCode className="h-4 w-4" /></button>
        <button onClick={() => onDelete(c)} className="py-2.5 px-4 rounded-lg text-rose-400 border hover:bg-rose-500/10" style={{ borderColor: "var(--pk-border)" }}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function GridView({ cards, onEdit, onDelete, adjustQty, recordSale, selected, onToggle, onPrint }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {cards.map((c) => (
        <div key={c.id} data-testid="card-row-item" className="rounded-xl border overflow-hidden group animate-fade-up" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
          <div className="aspect-[3/4] relative bg-[var(--pk-card)] flex items-center justify-center overflow-hidden">
            {c.image_url ? <img src={imgUrl(c.image_url)} alt="" className="w-full h-full object-cover" /> : <Package className="h-8 w-8 text-slate-700" />}
            <button onClick={() => onToggle(c.id)} data-testid="card-select-checkbox" className="absolute top-2 left-2 h-6 w-6 rounded bg-black/50 flex items-center justify-center text-white">
              {selected?.has(c.id) ? <CheckSquare className="h-4 w-4 text-amber-400" /> : <Square className="h-4 w-4" />}
            </button>
            <div className="absolute bottom-2 left-2"><GradeOrCond card={c} /></div>
            <div className="absolute top-2 right-2"><PlatformBadges card={c} /></div>
          </div>
          <div className="p-3">
            <div className="text-sm text-slate-100 font-medium truncate">{c.name}{GAME_BADGE[c.category] && <span className="ml-1.5 px-1 py-0.5 rounded bg-indigo-950/80 border border-indigo-800/60 text-[9px] font-bold text-indigo-300">{GAME_BADGE[c.category]}</span>}</div>
            <div className="text-[11px] text-slate-500 truncate">{c.set_name || "—"}</div>
            <div className="flex items-center justify-between mt-2">
              <span className="font-mono-num text-sm text-amber-400 inline-flex items-center">{fmt(c.tcg_price)}<SourceTag card={c} /></span>
              <QtyControls card={c} adjustQty={adjustQty} />
            </div>
            <div className="mt-2"><SellButtons card={c} recordSale={recordSale} /></div>
            <div className="flex gap-1 mt-2">
              <button onClick={() => onEdit(c)} data-testid="card-edit-button" className="flex-1 py-1.5 rounded text-xs text-slate-300 border hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>Edit</button>
              <button onClick={() => onPrint(c)} data-testid="card-print-qr-button" title="Print QR" className="py-1.5 px-2 rounded text-xs text-cyan-400 border hover:bg-cyan-500/10" style={{ borderColor: "var(--pk-border)" }}><QrCode className="h-3.5 w-3.5" /></button>
              <button onClick={() => onDelete(c)} className="py-1.5 px-2 rounded text-xs text-rose-400 border hover:bg-rose-500/10" style={{ borderColor: "var(--pk-border)" }}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Pagination({ page, totalPages, total, pageSize, onPage, onPageSize, count }) {
  const pages = pageWindow(page, totalPages);
  const start = (page - 1) * pageSize + 1;
  const end = start + count - 1;
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mt-5" data-testid="inventory-pagination">
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">
          Showing <span className="text-slate-300 font-mono-num">{start}-{end}</span> of <span className="text-slate-300 font-mono-num">{total}</span>
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Per page</span>
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} data-testid="inventory-page-size-select"
            className="px-2 py-1.5 rounded-lg border text-sm text-slate-200 outline-none" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
            {[50, 100, 200, 500].map((n) => <option key={n} value={n} style={{ background: "#0D1017" }}>{n}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} data-testid="inventory-page-prev"
          className="h-8 w-8 rounded-lg border flex items-center justify-center text-slate-400 hover:bg-[var(--pk-hover)] disabled:opacity-30" style={{ borderColor: "var(--pk-border)" }}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        {pages.map((p, i) => p === "…" ? (
          <span key={`e${i}`} className="px-2 text-slate-600 text-sm">…</span>
        ) : (
          <button key={p} onClick={() => onPage(p)} data-testid={`inventory-page-${p}`}
            className={`h-8 min-w-8 px-2 rounded-lg border text-sm font-mono-num ${p === page ? "bg-amber-500 text-[#1a1205] border-amber-500 font-semibold" : "text-slate-300 hover:bg-[var(--pk-hover)]"}`}
            style={p === page ? {} : { borderColor: "var(--pk-border)" }}>
            {p}
          </button>
        ))}
        <button onClick={() => onPage(page + 1)} disabled={page >= totalPages} data-testid="inventory-page-next"
          className="h-8 w-8 rounded-lg border flex items-center justify-center text-slate-400 hover:bg-[var(--pk-hover)] disabled:opacity-30" style={{ borderColor: "var(--pk-border)" }}>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function pageWindow(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = [1];
  const lo = Math.max(2, page - 1);
  const hi = Math.min(totalPages - 1, page + 1);
  if (lo > 2) pages.push("…");
  for (let i = lo; i <= hi; i++) pages.push(i);
  if (hi < totalPages - 1) pages.push("…");
  pages.push(totalPages);
  return pages;
}

function Empty({ onAdd }) {
  return (
    <div className="rounded-2xl border border-dashed flex flex-col items-center justify-center py-20 text-center" style={{ borderColor: "var(--pk-border)" }}>
      <div className="holo-shine h-14 w-14 rounded-2xl flex items-center justify-center border mb-4" style={{ borderColor: "var(--pk-border)" }}><Package className="h-6 w-6 text-amber-400" /></div>
      <h3 className="font-heading text-lg font-bold text-slate-200">No cards yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm">Add a card manually, import a TCGPlayer CSV, or sync from Shopify to get started.</p>
      <button onClick={onAdd} className="mt-5 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400"><Plus className="h-4 w-4" /> Add your first card</button>
    </div>
  );
}
